// Layout: spacing, font, alignment, margins, bullets and page breaks. Settings
// are saved on the resume as ResumeLayout and applied to the Word file on every
// render, so a template or colour switch keeps them.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { sectionTitles, SUMMARY_TITLE } from './resumeSections';

const LINE_SPACING = [['', 'Template default'], ['1', 'Single (1.0)'], ['1.08', '1.08'], ['1.15', '1.15'], ['1.25', '1.25'], ['1.5', '1.5'], ['2', 'Double (2.0)']];
const FONTS = ['', 'Aptos', 'Arial', 'Calibri', 'Cambria', 'Garamond', 'Georgia', 'Helvetica', 'Tahoma', 'Times New Roman', 'Verdana'];
const SIZES = ['', '9', '9.5', '10', '10.5', '11', '11.5', '12'];
const BULLETS: Array<[string, string]> = [['', 'Template default'], ['•', '• Round'], ['▪', '▪ Square'], ['–', '– Dash'], ['➤', '➤ Arrow']];
const ALIGNS = [['', 'Default'], ['left', 'Left'], ['center', 'Center'], ['right', 'Right'], ['both', 'Justified']];
const MARGINS: Array<[keyof Margins, string]> = [['top', 'Top'], ['bottom', 'Bottom'], ['left', 'Left'], ['right', 'Right']];
type Margins = { top?: number; right?: number; bottom?: number; left?: number };

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="flex items-center justify-between gap-3 text-sm">
    <span className="text-foreground">{label}</span>
    {children}
  </label>
);
const selectCls = 'h-8 w-40 rounded-md border bg-background px-2 text-sm';
const Heading = ({ children }: { children: React.ReactNode }) => <h4 className="text-sm font-semibold text-foreground">{children}</h4>;

export function LayoutPanel({ resumeJson, onApply }: { resumeJson: any; onApply: (next: any) => Promise<void> }) {
  const [layout, setLayout] = useState<any>(resumeJson?.ResumeLayout ?? {});
  useEffect(() => { setLayout(resumeJson?.ResumeLayout ?? {}); }, [resumeJson?.ResumeLayout]);
  const [section, setSection] = useState(SUMMARY_TITLE);
  const [saving, setSaving] = useState(false);

  const titles = [SUMMARY_TITLE, ...sectionTitles(resumeJson)];
  const set = (patch: any) => setLayout((l: any) => ({ ...l, ...patch }));
  const setBy = (field: string, title: string, value: any) => setLayout((l: any) => {
    const map = { ...(l[field] ?? {}) };
    if (value === '' || value === undefined) delete map[title]; else map[title] = value;
    return { ...l, [field]: map };
  });
  const para = layout.paragraphSpacing?.[section] ?? {};
  const setPara = (k: 'beforePt' | 'afterPt', v: string) => {
    const next = { ...para };
    if (v === '') delete next[k]; else next[k] = Number(v);
    setBy('paragraphSpacing', section, Object.keys(next).length ? next : undefined);
  };
  const margins: Margins = layout.marginsIn ?? {};
  const setMargin = (k: keyof Margins, v: string) => {
    const next = { ...margins };
    if (v === '') delete next[k]; else next[k] = Number(v);
    set({ marginsIn: Object.keys(next).length ? { top: 0.3, bottom: 0.2, left: 0.5, right: 0.5, ...next } : undefined });
  };
  const breaks: string[] = layout.pageBreakBefore ?? [];

  const apply = async (next: any) => {
    setSaving(true);
    try {
      const clean = JSON.parse(JSON.stringify(next));
      const json = { ...resumeJson };
      if (Object.keys(clean).length) json.ResumeLayout = clean; else delete json.ResumeLayout;
      await onApply(json);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Heading>Line spacing</Heading>
        <Row label="Whole resume">
          <select aria-label="Line spacing" className={selectCls} value={String(layout.lineSpacing ?? '')} onChange={(e) => set({ lineSpacing: e.target.value ? Number(e.target.value) : undefined })}>
            {LINE_SPACING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
      </div>

      <div className="space-y-2">
        <Heading>Section settings</Heading>
        <Row label="Section">
          <select aria-label="Section" className={selectCls} value={section} onChange={(e) => setSection(e.target.value)}>
            {titles.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Row>
        <Row label="Line spacing">
          <select aria-label="Section line spacing" className={selectCls} value={String(layout.sectionLineSpacing?.[section] ?? '')} onChange={(e) => setBy('sectionLineSpacing', section, e.target.value ? Number(e.target.value) : '')}>
            {LINE_SPACING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        <Row label="Space before each line (pt)">
          <input aria-label="Space before" type="number" min={0} max={24} step={1} className="h-8 w-20 rounded-md border bg-background px-2 text-sm" value={para.beforePt ?? ''} onChange={(e) => setPara('beforePt', e.target.value)} />
        </Row>
        <Row label="Space after each line (pt)">
          <input aria-label="Space after" type="number" min={0} max={24} step={1} className="h-8 w-20 rounded-md border bg-background px-2 text-sm" value={para.afterPt ?? ''} onChange={(e) => setPara('afterPt', e.target.value)} />
        </Row>
        <Row label="Alignment">
          <select aria-label="Alignment" className={selectCls} value={layout.align?.[section] ?? ''} onChange={(e) => setBy('align', section, e.target.value)}>
            {ALIGNS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        {section !== SUMMARY_TITLE && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={breaks.includes(section)} onChange={(e) => set({ pageBreakBefore: e.target.checked ? [...breaks, section] : breaks.filter((b) => b !== section) })} />
            Start this section on a new page
          </label>
        )}
      </div>

      <div className="space-y-2">
        <Heading>Space between sections</Heading>
        <Row label="Above each section bar (pt)">
          <input aria-label="Section gap" type="number" min={0} max={36} step={1} className="h-8 w-20 rounded-md border bg-background px-2 text-sm" value={layout.sectionGapPt ?? ''} onChange={(e) => set({ sectionGapPt: e.target.value === '' ? undefined : Number(e.target.value) })} />
        </Row>
      </div>

      <div className="space-y-2">
        <Heading>Font</Heading>
        <Row label="Font">
          <select aria-label="Font" className={selectCls} value={layout.fontFamily ?? ''} onChange={(e) => set({ fontFamily: e.target.value || undefined })}>
            {FONTS.map((f) => <option key={f} value={f}>{f || 'Template default'}</option>)}
          </select>
        </Row>
        <Row label="Body text size">
          <select aria-label="Font size" className={selectCls} value={String(layout.fontSizePt ?? '')} onChange={(e) => set({ fontSizePt: e.target.value ? Number(e.target.value) : undefined })}>
            {SIZES.map((f) => <option key={f} value={f}>{f ? `${f} pt` : 'Template default'}</option>)}
          </select>
        </Row>
      </div>

      <div className="space-y-2">
        <Heading>Page margins (inches)</Heading>
        <div className="grid grid-cols-2 gap-2">
          {MARGINS.map(([k, l]) => (
            <label key={k} className="flex items-center justify-between gap-2 text-sm">
              {l}
              <input aria-label={`${l} margin`} type="number" min={0.1} max={2} step={0.05} placeholder="default" className="h-8 w-24 rounded-md border bg-background px-2 text-sm" value={margins[k] ?? ''} onChange={(e) => setMargin(k, e.target.value)} />
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Heading>Bullets</Heading>
        <Row label="Style">
          <select aria-label="Bullet style" className={selectCls} value={layout.bulletChar ?? ''} onChange={(e) => set({ bulletChar: e.target.value || undefined })}>
            {BULLETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        <Row label="Indent (inches)">
          <input aria-label="Bullet indent" type="number" min={0.1} max={1} step={0.05} placeholder="0.3" className="h-8 w-20 rounded-md border bg-background px-2 text-sm" value={layout.bulletIndentTw ? layout.bulletIndentTw / 1440 : ''} onChange={(e) => set({ bulletIndentTw: e.target.value === '' ? undefined : Math.round(Number(e.target.value) * 1440) })} />
        </Row>
      </div>

      <p className="text-xs text-muted-foreground">The line rules (rectangle summary, two-line highlights, one-line bullets) are measured on the standard font and margins. Changing those may need a new fit.</p>

      <div className="flex gap-2">
        <Button size="sm" disabled={saving} onClick={() => apply(layout)} className="gap-1.5">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Apply to Word doc
        </Button>
        <Button size="sm" variant="outline" disabled={saving} onClick={() => { const keep = { sectionOrder: layout.sectionOrder, headingRenames: layout.headingRenames, textStyles: layout.textStyles }; setLayout(keep); apply(keep); }}>
          Reset to template
        </Button>
      </div>
    </div>
  );
}
